import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { OverviewBarChart } from "@/dashboard/components/overview";
import { useAppStore } from "@/stores";
import { Switch, useLocation } from "wouter";
import { Scope } from "./components/scope";
import { RecentSalesCard } from "./cards";
import {
  getDealsForDateRange,
  // getKeyDealsFromProjectedCommissions,
  getUsersForScope,
  getStalledDeals,
  getCommissionForDateRange,
  getKeyDealsFromOpenDeals,
} from "@revelate/calc";
import useSWR from "swr";
import { TableType, get, getAll } from "@/lib/supabase";
import { Loader } from "@/components/Loader";
import { CommissionKeyValues } from "./components/commission-key-values";
// import { DealKeyValues } from "./components/deal-key-values";
import { KeyDealsSelectedPeriod } from "./components/key-deals";
import { StalledDeals } from "./components/stalled-deals";
import {
  ComparisonSpeedometer,
  TargetSpeedometer,
} from "./components/target-speedometer";
import { useCommissionsData } from "@/hooks/useCommissionsData";
import { ErrorWithRetry } from "@/components/ErrorWithRetry";
import {
  NestedDeal,
  SignedInCompany,
  NestedUser,
} from "@revelate/types";
import { useMemo } from "react";
import { useCommissionsStream } from "@/hooks/useCommissionsStream";
import { BusinessDaysLeftInQuarter } from "./components/working-days-quarter";

export default function DashboardPage() {
  const { currentUser, companyCurrency, scope, dateRange } = useAppStore(
    (state) => state
  );

  // Fetch company data directly
  const {
    data: company,
    error: companyError,
    isLoading: companyLoading,
  } = useSWR(
    [
      "companies" as TableType,
      currentUser ? currentUser?.company_id?.toString() : null,
    ],
    ([companiesTable, companyId]): Promise<SignedInCompany> =>
      get(companiesTable, companyId),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  // Get user IDs from company data
  const userIds = useMemo(() => {
    if (!company || !company.users) return [];
    return company?.users
      ?.filter(
        (user: NestedUser) =>
          user.include_in_calculations && user.is_active !== false
      )
      ?.map((user: NestedUser) => user.id);
  }, [company]);

  // Fetch commissions data directly using useCommissionsData
  const {
    users,
    error: commissionsError,
    isLoading: commissionsLoading,
  } = useCommissionsData({
    currentUser,
    dateRange,
    userIds,
    enabled: !!userIds.length,
  });

  const {
    data: projectedUsersWithCommissions,
    // error: projectedUsersWithCommissionsError,
    isLoading: projectedUsersWithCommissionsLoading,
    // progress: projectedUsersWithCommissionsLoadingProgress,
  } = useCommissionsStream({
    currentUser,
    dateRange,
    userIds,
    isProjected: true,
    isEphemeral: true, // Don't interact with the database
    enabled: !!userIds.length,
  });

  // Fetch deals data
  const {
    data: deals,
    error: dealsError,
    isLoading: dealsLoading,
  } = useSWR(
    [
      "deals" as TableType,
      currentUser ? currentUser?.company_id?.toString() : null,
    ],
    ([dealsTable, companyId]): Promise<NestedDeal[]> =>
      getAll(dealsTable, companyId),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  // -----------
  // Scoped data
  // -----------

  // const scopedDeals = useMemo(() => {
  //   if (!deals || !deals.length) return [];
  //
  //   return getDealsForDateRange({
  //     deals: deals || [],
  //     dateRange,
  //     scope,
  //   }) as NestedDeal[];
  // }, [deals, dateRange, scope]);

  const scopedProjectedDeals = useMemo(() => {
    if (!deals || !deals.length) return [];

    return getDealsForDateRange({
      deals: deals || [],
      dateRange,
      scope,
      isProjected: true,
    }) as NestedDeal[];
  }, [deals, dateRange, scope]);

  const stalledDeals = useMemo(() => {
    return getStalledDeals(scopedProjectedDeals || []);
  }, [scopedProjectedDeals]);

  const scopedUsers = useMemo(() => {
    return company ? getUsersForScope(company, scope) : [];
  }, [company, scope]);

  const commissions = users?.flatMap((user) => user.commissions);
  const scopedCommissions = commissions?.filter((commission) =>
    scopedUsers?.map((u) => u.id)?.includes(commission.user_id)
  );

  const scopedProjectedUsers = useMemo(() => {
    if (
      !scopedUsers ||
      !scopedUsers.length ||
      !projectedUsersWithCommissions ||
      !projectedUsersWithCommissions.length ||
      projectedUsersWithCommissionsLoading
    ) {
      return [];
    }

    const scopedUserIds = new Set(scopedUsers.map((u) => u.id));
    return projectedUsersWithCommissions
      .filter((u) => scopedUserIds.has(u.id));
  }, [
    scopedUsers,
    projectedUsersWithCommissions,
    projectedUsersWithCommissionsLoading,
  ]);

  const scopedProjectedCommissions = useMemo(() => {
    if (
      !scopedProjectedUsers ||
      !scopedProjectedUsers.length
    ) {
      return [];
    }

    return scopedProjectedUsers.flatMap((u) => u.commissions ?? []);
  }, [scopedProjectedUsers]);

  const scopedUserMonthlyCommissionTargets = scopedUsers
    ?.map((user) => user.commission_target)
    ?.filter((t) => t !== null);

  const projectedCommissionsSelectedPeriod = useMemo(() => {
    if (!scopedProjectedCommissions || !scopedProjectedCommissions.length) return [];
    return getCommissionForDateRange(scopedProjectedCommissions, dateRange);
  }, [scopedProjectedCommissions]);

  // const keyDealsSelectedPeriod = useMemo(() => {
  //   return getKeyDealsFromProjectedCommissions(
  //     scopedUsers,
  //     groupCommissionsByDealName(projectedCommissionsSelectedPeriod || [])
  //   );
  // }, [scopedUsers, projectedCommissionsSelectedPeriod]);

  const keyDealsSelectedPeriod = useMemo(() => {
    return getKeyDealsFromOpenDeals(
      scopedProjectedDeals,
    );
  }, [scopedProjectedDeals]);

  if (commissionsLoading || companyLoading) {
    return <Loader progress={100} />;
  }

  if (commissionsError || companyError) {
    return (
      <ErrorWithRetry
        error={commissionsError || companyError?.message || "An error occurred"}
        onRetry={() => {
          window.location.reload();
        }}
      />
    );
  }

  return (
    <div className="flex-col md:flex">
      <div className="flex-1 space-y-2 py-0">

        {/* Top section */}
        <div className="flex items-center justify-between">
          <Scope />
          <BusinessDaysLeftInQuarter />
        </div>

        {/* Main page body */}
        <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">

          {/* Left column */}
          <div className="col-span-3 flex flex-col gap-4 mt-2">
            <CommissionKeyValues
              commissions={scopedCommissions || []}
              projectedCommissions={projectedCommissionsSelectedPeriod || []}
              companyCurrency={companyCurrency}
              showCards={["earned", "forecasted"]}
            />
            <Card>
              <CardHeader>
                <CardTitle>Commission by month</CardTitle>
                <CardDescription>
                  The commission earned per month
                </CardDescription>
              </CardHeader>
              <CardContent className="pl-3 pb-3">
                <OverviewBarChart
                  earnedCommissions={scopedCommissions || []}
                  projectedCommissions={projectedCommissionsSelectedPeriod || []}
                />
              </CardContent>
            </Card>
            <div className="flex flex-col gap-4">
              <KeyDealsSelectedPeriod
                dateRange={dateRange}
                keyDeals={keyDealsSelectedPeriod}
                scope={scope}
              />
              <StalledDeals
                deals={stalledDeals}
                scope={scope}
              />
            </div>
          </div>

          {/* Right column */}
          <div className="col-span-3 flex flex-col gap-4 mt-2">
            {(scope?.type === "company" || scope?.type === "team") && (
              <CommissionKeyValues
                commissions={scopedCommissions || []}
                projectedCommissions={scopedProjectedCommissions || []}
                companyCurrency={companyCurrency}
                showCards={["unapproved", "paid"]}
              />
            )}

            <Switch>
              {scope?.type === "user" && (
                (company as SignedInCompany)?.speedometer ===
                  "target_attainment" ? (
                  scopedUserMonthlyCommissionTargets.length > 0 && (
                    <TargetSpeedometer
                      userCommissions={scopedCommissions || []}
                      usersMonthlyTargets={
                        scopedUserMonthlyCommissionTargets
                      }
                      scope={scope}
                      dateRange={dateRange}
                      companyCurrency={companyCurrency}
                    />
                  )
                ) : (
                  <ComparisonSpeedometer
                    comparisonCommissions={commissions || []}
                    userCommissions={scopedCommissions || []}
                    scope={scope}
                    companyCurrency={companyCurrency}
                  />
                )
              )}

              {scope?.type === "team" && (
                <ComparisonSpeedometer
                  comparisonCommissions={commissions || []}
                  userCommissions={scopedCommissions || []}
                  scope={scope}
                  companyCurrency={companyCurrency}
                />
              )}

              <RecentSalesCard
                scopedUsers={
                  users?.filter((user) =>
                    scopedUsers?.map((u) => u.id)?.includes(user.id)
                  ) || []
                }
                projectedUsers={scopedProjectedUsers}
              />
            </Switch>
          </div>
        </div>
      </div>
    </div>
  );
}
